import { useState } from "react";
import type { Direction } from "../../api/contract";
import { Textarea } from "@bindery/ui/components/ui/textarea";
import { Button } from "@bindery/ui/components/ui/button";
import { DrillCoverageHint } from "./drill-coverage-hint";
import { MorphologyDisplay } from "../n5/morphology-display";
import { useDrillKeyboard } from "../../hooks/use-drill-keyboard";

interface DrillPromptProps {
  direction: Direction;
  sentence: string;
  translation: string;
  glosses: { surface: string; pos: string; gloss: string }[];
  stretchLemmas: string[];
  round: 1 | 2;
  /** Pre-fills the textarea — used to restore the user's answer after a
   * grading-error retry so they don't have to retype it. */
  initialAttempt?: string;
  onSubmit: (attempt: string) => void;
}

export function DrillPrompt({
  direction,
  sentence,
  translation,
  glosses,
  stretchLemmas,
  round,
  initialAttempt = "",
  onSubmit,
}: DrillPromptProps) {
  const [attempt, setAttempt] = useState(initialAttempt);

  const handleSubmit = () => {
    if (attempt.trim().length === 0) return;
    onSubmit(attempt.trim());
  };

  useDrillKeyboard({
    enabled: true,
    onSubmit: handleSubmit,
  });

  const prompt = direction === "ja_to_en" ? sentence : translation;
  const targetLabel = direction === "ja_to_en" ? "English" : "Japanese";
  const directionLabel = direction === "ja_to_en" ? "JA → EN" : "EN → JA";

  return (
    <div className="drill-card-enter flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="bg-muted rounded px-2 py-0.5 text-xs font-medium">
          {directionLabel}
        </span>
        <span className="text-muted-foreground text-xs">
          Round {round} of 2
        </span>
      </div>

      <div className="bg-card rounded-lg border p-4">
        <p className="text-lg leading-relaxed">{prompt}</p>
      </div>

      {glosses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {glosses.map((g) => (
            <span
              key={g.surface}
              className="rounded-full border px-2.5 py-0.5 text-xs"
            >
              <span className="font-medium">
                <MorphologyDisplay display={g.surface} pos={g.pos} size="sm" />
              </span>
              <span className="text-muted-foreground"> {g.gloss}</span>
            </span>
          ))}
        </div>
      )}

      <DrillCoverageHint unknownCount={stretchLemmas.length} />

      <Textarea
        value={attempt}
        onChange={(e) => setAttempt(e.target.value)}
        placeholder={`Type your ${targetLabel} translation...`}
        className="min-h-24 resize-none"
        // oxlint-disable-next-line jsx-a11y/no-autofocus -- the drill is the answer field; each prompt is opened to be typed into
        autoFocus
      />

      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-xs">
          Cmd/Ctrl + Enter to submit
        </p>
        <Button
          onClick={handleSubmit}
          disabled={attempt.trim().length === 0}
          size="sm"
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
